import { useEffect, useState } from "react";
import { Check, ChevronLeft, ChevronRight, Send, CheckCircle2, RotateCcw, QrCode } from "lucide-react";
import { PageFooter } from "@/components/PageFooter";
import { StepMarket } from "@/components/setup/StepMarket";
import { StepBrand } from "@/components/setup/StepBrand";
import { StepQrMenu } from "@/components/setup/StepQrMenu";
import { StepTeam } from "@/components/setup/StepTeam";
import { StepAssets } from "@/components/setup/StepAssets";
import { StepReview } from "@/components/setup/StepReview";
import { AssetDelivery } from "@/components/setup/AssetDelivery";
import { useLang } from "@/i18n";

const API = process.env.REACT_APP_BACKEND_URL;
const DRAFT_KEY = "fora_country_setup_draft_v1";

const EMPTY_FORM = {
  country: "",
  bu_owner_name: "",
  bu_owner_email: "",
  it_liaison_name: "",
  it_liaison_email: "",
  spoc_name: "",
  spoc_email: "",
  default_language: "",
  additional_languages: "",
  currency: "",
  brand_color: "",
  brand_notes: "",
  outlets_estimate: "",
  segments: [],
  print_agency: "",
  print_contact: "",
  golive_target: "",
  additional_requirements: "",
  training_assignments: [],
  main_touchpoints: [],
  banner_plan: "",
  asset_files: [],
};

const STEPS = [
  { id: "market", label: "Market & Contacts" },
  { id: "brand", label: "Brand & Localisation" },
  { id: "qrmenu", label: "QR Menu Setup" },
  { id: "team", label: "Team & Touchpoints" },
  { id: "assets", label: "Assets" },
  { id: "review", label: "Review & Submit" },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const loadDraft = () => {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return EMPTY_FORM;
    return { ...EMPTY_FORM, ...JSON.parse(raw) };
  } catch (e) {
    return EMPTY_FORM;
  }
};

export const CountrySetup = ({ onNavigate }) => {
  const { t } = useLang();
  const [form, setForm] = useState(loadDraft);
  const [mode, setMode] = useState("wizard");
  const [step, setStep] = useState(0);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitted, setSubmitted] = useState(null);

  useEffect(() => {
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify(form));
    } catch (e) {
      // storage full / unavailable — draft simply won't persist
    }
  }, [form]);

  const update = (patch) => {
    setForm((f) => ({ ...f, ...patch }));
    setErrors((e) => {
      const next = { ...e };
      Object.keys(patch).forEach((k) => delete next[k]);
      return next;
    });
  };

  const validateStep = (idx) => {
    const errs = {};
    if (idx === 0) {
      if (!form.country) errs.country = "Please select your market";
      if (!form.bu_owner_name.trim()) errs.bu_owner_name = "Required";
      if (!EMAIL_RE.test(form.bu_owner_email)) errs.bu_owner_email = "Valid email required";
    }
    if (idx === 1) {
      if (!form.default_language.trim()) errs.default_language = "Required";
      if (!form.currency.trim()) errs.currency = "Required";
    }
    if (idx === 2) {
      if (!form.outlets_estimate.trim()) errs.outlets_estimate = "Required";
      if (!form.print_agency) errs.print_agency = "Please choose one option";
      if (form.print_agency === "own_agency" && !form.print_contact.trim())
        errs.print_contact = "Please enter the print contact or agency name";
    }
    if (idx === 3) {
      const badEmail = (list) => list.some((p) => p.email.trim() && !EMAIL_RE.test(p.email.trim()));
      if (badEmail(form.training_assignments)) errs.training_assignments = "Please enter valid email addresses";
      if (badEmail(form.main_touchpoints)) errs.main_touchpoints = "Please enter valid email addresses";
    }
    return errs;
  };

  const goNext = () => {
    const errs = validateStep(step);
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    setErrors({});
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
    window.scrollTo({ top: 0, behavior: "instant" });
  };

  const goToStep = (idx) => {
    setErrors({});
    setStep(idx);
    window.scrollTo({ top: 0, behavior: "instant" });
  };

  const resetForm = () => {
    if (!window.confirm(t("Clear all entered data and start over?"))) return;
    localStorage.removeItem(DRAFT_KEY);
    setForm(EMPTY_FORM);
    setStep(0);
    setErrors({});
    setSubmitError("");
  };

  const submit = async () => {
    setSubmitting(true);
    setSubmitError("");
    try {
      const cleanPeople = (list) => list.filter((p) => p.role.trim() || p.name.trim() || p.email.trim());
      const payload = {
        ...form,
        additional_languages: form.additional_languages
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
        training_assignments: cleanPeople(form.training_assignments),
        main_touchpoints: cleanPeople(form.main_touchpoints),
        portal_origin: window.location.origin,
      };
      const res = await fetch(`${API}/api/country-onboarding`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        let msg = `Submission failed (${res.status})`;
        if (Array.isArray(err.detail)) {
          msg = err.detail.map((d) => `${(d.loc || []).slice(1).join(".")}: ${d.msg}`).join(" · ");
        } else if (typeof err.detail === "string") {
          msg = err.detail;
        }
        throw new Error(msg);
      }
      const data = await res.json();
      localStorage.removeItem(DRAFT_KEY);
      setSubmitted(data);
      window.scrollTo({ top: 0, behavior: "instant" });
    } catch (e) {
      setSubmitError(e.message || "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  /* ---------- Success screen ---------- */
  if (submitted) {
    return (
      <div data-testid="country-setup-success">
        <div className="bg-white border border-[#e8e8e8] rounded-sm p-10 sm:p-14 text-center max-w-2xl mx-auto mt-8">
          <div className="w-16 h-16 rounded-full bg-emerald-500 flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-8 h-8 text-white" strokeWidth={2} />
          </div>
          <span className="cchbc-eyebrow justify-center">{t("Submission received")}</span>
          <h1 className="font-condensed text-3xl sm:text-4xl font-black uppercase tracking-tight text-[#000000] mt-4 mb-4">
            {submitted.country} — {t("Setup Submitted")}
          </h1>
          <p className="text-sm text-[#555555] leading-relaxed max-w-md mx-auto">
            {t("The Master F&B onboarding team has been notified at")} <b>info@master-fb.com</b> {t("and will contact")}{" "}
            <b>{submitted.bu_owner_email}</b> {t("with next steps within 2 business days.")}
          </p>
          <p className="text-xs text-[#999999] mt-4" data-testid="country-setup-submission-id">
            {t("Submission ID:")} {submitted.id.slice(0, 8).toUpperCase()}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3 mt-8">
            <button onClick={() => onNavigate("onboarding")} className="cchbc-btn cchbc-btn-primary" data-testid="success-back-onboarding-btn">
              {t("View Onboarding Process")}
            </button>
            <button onClick={() => onNavigate("home")} className="cchbc-btn cchbc-btn-outline" data-testid="success-back-home-btn">
              {t("All Products")}
            </button>
          </div>
        </div>
        <PageFooter />
      </div>
    );
  }

  /* ---------- Wizard ---------- */
  const isLast = step === STEPS.length - 1;
  const stepProps = { form, update, errors };

  return (
    <div data-testid="country-setup-view">
      {/* Title */}
      <div className="mb-8">
        <span className="cchbc-eyebrow mb-5">{t("QR Menu — First Product")}</span>
        <h1
          className="font-condensed text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tight text-[#000000] mb-4 mt-4 leading-[0.95]"
          data-testid="country-setup-title"
        >
          {t("Country Setup")}
        </h1>
        <p className="text-[#555555] text-base max-w-2xl leading-relaxed">
          {t("Send Master F&B everything needed to prepare QR Menu for your market — contacts, localisation, requirements and brand assets. Takes about 10 minutes.")}
        </p>
        <div className="inline-flex border border-[#e8e8e8] rounded-sm overflow-hidden mt-5" data-testid="setup-mode-toggle">
          <button
            onClick={() => setMode("wizard")}
            className={`px-4 py-2.5 text-[11px] font-bold uppercase tracking-[0.1em] transition-colors ${
              mode === "wizard" ? "bg-[#000000] text-white" : "bg-white text-[#555555] hover:text-[#000000]"
            }`}
            data-testid="setup-mode-wizard-btn"
          >
            {t("Full country setup")}
          </button>
          <button
            onClick={() => setMode("assets")}
            className={`px-4 py-2.5 text-[11px] font-bold uppercase tracking-[0.1em] transition-colors border-l border-[#e8e8e8] ${
              mode === "assets" ? "bg-[#000000] text-white" : "bg-white text-[#555555] hover:text-[#000000]"
            }`}
            data-testid="setup-mode-assets-btn"
          >
            {t("Assets only — send updates")}
          </button>
        </div>
        {mode === "wizard" && (
          <div className="flex items-center gap-4 mt-4">
            <span className="inline-flex items-center gap-1.5 text-[11px] text-[#999999]">
              <QrCode className="w-3.5 h-3.5" /> {t("Draft is saved automatically on this device")}
            </span>
            <button
              onClick={resetForm}
              className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-[#555555] hover:text-[#dd260e] transition-colors"
              data-testid="country-setup-reset-btn"
            >
              <RotateCcw className="w-3 h-3" /> {t("Reset form")}
            </button>
          </div>
        )}
      </div>

      {mode === "assets" ? (
        <>
          <AssetDelivery />
          <PageFooter />
        </>
      ) : (
        <>
      {/* Step indicator — desktop */}
      <div className="hidden sm:flex items-center mb-8" data-testid="country-setup-steps">
        {STEPS.map((s, idx) => {
          const done = idx < step;
          const current = idx === step;
          return (
            <div key={s.id} className="flex items-center">
              <button onClick={() => idx < step && goToStep(idx)} className="flex flex-col items-center" disabled={idx > step}>
                <span
                  className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
                    done
                      ? "bg-[#000000] text-white cursor-pointer"
                      : current
                      ? "bg-[#dd260e] text-white"
                      : "bg-[#e8e8e8] text-[#999999]"
                  }`}
                >
                  {done ? <Check className="w-4 h-4" strokeWidth={2.5} /> : idx + 1}
                </span>
                <span
                  className={`text-[10px] font-semibold uppercase tracking-wide mt-1.5 max-w-[90px] text-center leading-tight ${
                    current ? "text-[#000000]" : "text-[#999999]"
                  }`}
                >
                  {t(s.label)}
                </span>
              </button>
              {idx < STEPS.length - 1 && <div className={`w-10 lg:w-16 h-[2px] mx-2 mb-5 ${idx < step ? "bg-[#000000]" : "bg-[#e8e8e8]"}`} />}
            </div>
          );
        })}
      </div>

      {/* Step indicator — mobile */}
      <div className="sm:hidden mb-6" data-testid="country-setup-steps-mobile">
        <p className="text-xs font-bold uppercase tracking-[0.12em] text-[#dd260e]">
          {t("Step")} {step + 1} {t("of")} {STEPS.length}
        </p>
        <p className="font-condensed text-xl font-black uppercase text-[#000000] mt-0.5">{t(STEPS[step].label)}</p>
        <div className="w-full h-1.5 bg-[#e8e8e8] rounded-full overflow-hidden mt-2">
          <div className="h-full bg-[#dd260e] rounded-full transition-all" style={{ width: `${((step + 1) / STEPS.length) * 100}%` }} />
        </div>
      </div>

      {/* Step body */}
      <div className="bg-white border border-[#e8e8e8] rounded-sm p-6 sm:p-8">
        {step === 0 && <StepMarket {...stepProps} />}
        {step === 1 && <StepBrand {...stepProps} />}
        {step === 2 && <StepQrMenu {...stepProps} />}
        {step === 3 && <StepTeam {...stepProps} />}
        {step === 4 && (
          <StepAssets
            files={form.asset_files}
            onAdd={(meta) => setForm((f) => ({ ...f, asset_files: [...f.asset_files, meta] }))}
            onRemove={(fileId) => setForm((f) => ({ ...f, asset_files: f.asset_files.filter((x) => x.file_id !== fileId) }))}
          />
        )}
        {step === 5 && <StepReview form={form} goToStep={goToStep} />}
      </div>

      {/* Submit error */}
      {submitError && (
        <p className="text-sm text-[#dd260e] font-semibold mt-4" data-testid="country-setup-submit-error">
          {submitError}
        </p>
      )}

      {/* Footer nav */}
      <div className="flex items-center justify-between mt-6">
        <button
          onClick={() => goToStep(Math.max(0, step - 1))}
          disabled={step === 0}
          className={`cchbc-btn cchbc-btn-outline ${step === 0 ? "opacity-30 cursor-not-allowed" : ""}`}
          data-testid="country-setup-back-btn"
        >
          <ChevronLeft className="w-3.5 h-3.5" /> {t("Back")}
        </button>
        {isLast ? (
          <button onClick={submit} disabled={submitting} className="cchbc-btn cchbc-btn-primary" data-testid="country-setup-submit-btn">
            <Send className="w-3.5 h-3.5" />
            {submitting ? t("Submitting…") : t("Submit Country Setup")}
          </button>
        ) : (
          <button onClick={goNext} className="cchbc-btn cchbc-btn-primary" data-testid="country-setup-next-btn">
            {t("Next")} <ChevronRight className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

          <PageFooter />
        </>
      )}
    </div>
  );
};
